import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import API_BASE_URL from './config';
import './PersonGallery.css';
import { buildChunksByOrientation, pickCaptions } from './magazineLayout';
import useImageRatios from './useImageRatios';
import GalleryGrid from './GalleryGrid';
import StudioBrandBar from './StudioBrandBar';
import { themeOf, isGridLayout, themeClass } from './galleryThemes';
import { buildEventTitle, buildEventSubtitle } from './galleryTitles';

/**
 * The whole event, as a magazine spread.
 *
 * The share link's root is the working surface — a contact sheet with voting,
 * folder tabs and a selection counter — which is what you want while picking
 * photos and not what you want while looking at them. "View the full event
 * gallery" from the person and selection pages lands here instead: the same
 * editorial layout those pages use (shared via magazineLayout), wearing the
 * event's theme, pointed at every photo rather than one person's.
 *
 * Read-only by design, and ordered by upload so the day reads in sequence.
 */

const pluralize = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function EventGallery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const sentinelRef = useRef(null);

  useEffect(() => {
    // Same localStorage key ClientGallery uses, so a photo this viewer
    // unselected in the working gallery stays hidden here too.
    const clientId = localStorage.getItem('cg_clientId') || 'client_' + Date.now();
    localStorage.setItem('cg_clientId', clientId);

    const fetchPage = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/shared/${token}/gallery/page?clientId=${clientId}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'This gallery could not be found.' : 'Something went wrong loading this gallery.');
          setLoading(false);
          return;
        }
        setData(await res.json());
      } catch (e) {
        setError('Something went wrong loading this gallery.');
      }
      setLoading(false);
    };
    fetchPage();
  }, [token]);

  // How far the reader has got IS how much has been measured — a photo cannot
  // be given a slot before its shape is known.
  const allPhotos = data?.photos;
  const { ratios, measured, measureMore, hasMore } = useImageRatios(allPhotos);

  const spread = useMemo(() => {
    if (!data) return null;
    const ready = data.photos.slice(0, measured);
    const chunks = buildChunksByOrientation(ready, ratios);
    return { chunks, captions: pickCaptions(chunks.length) };
  }, [data, measured, ratios]);

  const shownChunks = spread?.chunks || [];
  const readyPhotos = useMemo(
    () => (data ? data.photos.slice(0, measured) : []),
    [data, measured]
  );

  useEffect(() => {
    if (!hasMore || typeof IntersectionObserver === 'undefined') return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) measureMore(); },
      { rootMargin: '600px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, measureMore]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const handleDownloadPhoto = async (e, photo) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_BASE_URL}/api/images/${photo.id}/download`);
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `photo-${photo.id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      showToast('Download failed — please try again');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = buildEventTitle(data.event);
    if (navigator.share) {
      try { await navigator.share({ title, text: buildEventSubtitle(data.event), url }); }
      catch (e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copied to clipboard!');
      } catch (e) {
        showToast('Could not copy link');
      }
    }
  };

  if (loading) {
    return (
      <div className="pg-status-page">
        <div className="pg-spinner" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="pg-status-page">
        <h1 className="pg-status-title">Gallery Not Found</h1>
        <p className="pg-status-desc">{error || 'This link may have expired.'}</p>
      </div>
    );
  }

  const { event, photos, studioName, logoUrl, bannerUrl } = data;
  // An unknown or missing theme falls back to the magazine — that is what
  // every event showed before themes existed.
  const theme = themeOf(event.gallery_theme);
  const isGrid = isGridLayout(event.gallery_theme);
  const title = buildEventTitle(event);
  const subtitle = buildEventSubtitle(event);

  if (photos.length === 0) {
    return (
      <div className="pg-status-page">
        <h1 className="pg-status-title">{title}</h1>
        <p className="pg-status-desc">There are no photos in this gallery yet.</p>
      </div>
    );
  }

  return (
    <div className={`pg-page ${themeClass(event.gallery_theme)}`}>
      <StudioBrandBar studioName={studioName} logoUrl={logoUrl} bannerUrl={bannerUrl} />
      <div className="pg-hero">
        {photos[0]?.image_url && <img className="pg-hero-bg" src={photos[0].image_url} alt="" />}
        <div className="pg-hero-scrim" />
        <div className="pg-hero-content">
          <h1 className="pg-title">{title}</h1>
          <p className="pg-desc">
            {pluralize(photos.length, 'photo')}
            {subtitle ? ` · ${subtitle}` : ''}
          </p>
          <div className="pg-actions">
            <button className="pg-btn pg-btn--primary" onClick={handleShare}>
              <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15"><path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.474l6.733-3.367A2.52 2.52 0 0 1 13 4.5Z"/></svg>
              Share
            </button>
          </div>
        </div>
      </div>

      {isGrid ? (
        <GalleryGrid photos={readyPhotos} ratios={ratios} onDownload={handleDownloadPhoto} />
      ) : (
        <div className="pg-spread">
          {shownChunks.map((chunk, ci) => (
            <React.Fragment key={`evt-${ci}`}>
              <div className={`pg-chunk pg-chunk--${chunk.layout}`}>
                {chunk.photos.map((photo) => (
                  <div key={photo.id} className="pg-chunk-tile">
                    <img
                      src={photo.image_url || photo.thumbnail_url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                    <button
                      className="pg-tile-download"
                      onClick={(e) => handleDownloadPhoto(e, photo)}
                      aria-label="Download photo"
                      title="Download this photo"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M10 12.5a.75.75 0 0 0 .53-.22l3.5-3.5a.75.75 0 0 0-1.06-1.06L10.75 9.94V3a.75.75 0 0 0-1.5 0v6.94L7.03 7.72a.75.75 0 0 0-1.06 1.06l3.5 3.5c.14.14.33.22.53.22ZM4.75 13a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 6 18.25h8A2.75 2.75 0 0 0 16.75 15.5V13a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H6c-.69 0-1.25-.56-1.25-1.25V13Z"/></svg>
                    </button>
                  </div>
                ))}
              </div>
              <div className="pg-words">
                <span className="pg-words-mark">{theme.mark}</span>
                <p className="pg-words-text">{spread.captions[ci]}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="pg-more">
          <div ref={sentinelRef} className="pg-more-sentinel" aria-hidden="true" />
          <button className="pg-more-btn" onClick={measureMore}>
            Show more photos
          </button>
        </div>
      )}

      <footer className="pg-footer">
        <p className="pg-footer-text">{pluralize(photos.length, 'photo')} · curated by Photentra AI</p>
        <Link to={`/shared/${token}`} className="pg-footer-link">Open the selection gallery →</Link>
      </footer>

      {toast && <div className="pg-toast">{toast}</div>}
    </div>
  );
}

export default EventGallery;
